import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Link,
  InputAdornment,
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import { Visibility, VisibilityOff, WhatsApp as WhatsAppIcon, Email as EmailIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as MensureLogo } from '../../../assets/logo.svg';
import { useTheme as useDesignTheme } from '../../../design-system/providers/ThemeProvider';

const LoginForm = () => {
  const theme = useTheme();
  const { primaryGradient } = useDesignTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [supportContacts, setSupportContacts] = useState(null);
  const { login, loginWithGoogle, error } = useAuth();
  const navigate = useNavigate();

  const BYPASS_ROLES = ['admin', 'mensure', 'curator', 'curador'];

  const handleRedirectAfterLogin = () => {
    setTimeout(() => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      if (user.role === 'admin' || user.role === 'mensure') {
        navigate('/admin/dashboard');
      } else if (!BYPASS_ROLES.includes(user.role?.toLowerCase()) && !user.activation?.isActivated) {
        // Usuário comum sem ativação → tela de token
        navigate('/activate');
      } else if (!BYPASS_ROLES.includes(user.role?.toLowerCase()) && !user.hasBrand) {
        // Ativado mas sem marca → onboarding
        navigate('/onboarding');
      } else {
        // Ativado com marca (ou bypass role) → plataforma
        navigate('/');
      }
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        handleRedirectAfterLogin();
      } else {
        setSupportContacts(result.contactInfo || null);
      }
    } catch (error) {
      console.error('Erro inesperado ao fazer login:', error);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        // useGoogleLogin retorna access_token, precisamos trocar por id_token
        // ou adaptar o backend para aceitar access_token
        const result = await loginWithGoogle(tokenResponse.access_token, 'access_token');

        if (result.success) {
          handleRedirectAfterLogin();
        } else {
          setSupportContacts(result.contactInfo || null);
        }
      } catch (error) {
        console.error('Erro no login com Google:', error);
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      console.error('Erro no login com Google');
    }
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 1.5, sm: 2 },
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}
    >
      <Paper
        elevation={2}
        sx={{
          padding: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: { xs: '100%', sm: 400 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxSizing: 'border-box',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <MensureLogo style={{
            height: 36,
            width: 'auto',
            fill: theme.palette.primary.main,
            color: theme.palette.primary.main
          }} />
        </Box>
        <Typography
          component="h1"
          variant="h5"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: 'text.primary',
            textAlign: 'center'
          }}
        >
          Mensure Monitor
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
          {error && (
            <Box
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 1.5,
                background: primaryGradient || 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                boxShadow: '0 4px 14px rgba(0,0,0,0.1)'
              }}
            >
              <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 36, mb: 1, opacity: 0.9 }} />
              
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                {error}
              </Typography>
              
              {supportContacts && (
                <Box sx={{ width: '100%' }}>
                  <Divider sx={{ mb: 1.5, borderColor: 'primary.contrastText', opacity: 0.2 }} />
                  <Typography variant="caption" sx={{ display: 'block', mb: 1.5, fontWeight: 700, opacity: 0.85 }}>
                    Fale com nosso time de atendimento:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', justifyContent: 'center' }}>
                    {supportContacts.whatsapp && (
                      <IconButton
                        size="large"
                        href={`https://wa.me/${supportContacts.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá, estou tentando acessar minha conta Mensure e apareceu essa mensagem de acesso inacessível. \n\nUsuário: ${supportContacts.userEmail}`)}`}
                        target="_blank"
                        sx={{
                          color: 'success.main',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        title="Falar no WhatsApp"
                      >
                        <WhatsAppIcon fontSize="inherit" />
                      </IconButton>
                    )}
                    {supportContacts.email && (
                      <IconButton
                        size="large"
                        href={`mailto:${supportContacts.email}?subject=Acesso Bloqueado&body=${encodeURIComponent(`Olá, \n\nEstou tentando acessar minha conta Mensure e apareceu essa mensagem de acesso inacessível. \n\nUsuário: ${supportContacts.userEmail}`)}`}
                        sx={{
                          color: 'primary.main',
                          transition: 'all 0.2s',
                          '&:hover': { transform: 'scale(1.1)' }
                        }}
                        title="Enviar e-mail de suporte"
                      >
                        <EmailIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="cliente@exemplo.com"
            InputLabelProps={{ shrink: true }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
              '& .MuiInputLabel-root': {
                position: 'relative',
                transform: 'none',
                marginBottom: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '& .MuiOutlinedInput-notchedOutline legend': {
                display: 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                top: 0,
              },
              // Sobrescrever o azul do autofill do navegador
              '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
                WebkitBoxShadow: `0 0 0 30px ${theme.palette.background.paper} inset !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                caretColor: theme.palette.text.primary,
                borderRadius: '12px',
              }
            }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Senha"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Digite sua senha"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
              '& .MuiInputLabel-root': {
                position: 'relative',
                transform: 'none',
                marginBottom: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '& .MuiOutlinedInput-notchedOutline legend': {
                display: 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                top: 0,
              },
              // Sobrescrever o azul do autofill do navegador
              '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
                WebkitBoxShadow: `0 0 0 30px ${theme.palette.background.paper} inset !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                caretColor: theme.palette.text.primary,
                borderRadius: '12px',
              }
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || googleLoading}
            sx={{
              mt: 1,
              mb: 2,
              borderRadius: 1.5,
              fontWeight: 500,
              py: 1.2,
              background: primaryGradient || 'primary.main',
              '&:hover': {
                background: primaryGradient || 'primary.dark',
                filter: primaryGradient ? 'brightness(0.9)' : 'none'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Entrar'
            )}
          </Button>

          {/* Divisor */}
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              ou
            </Typography>
          </Divider>

          {/* Login com Google */}
          <Button
            fullWidth
            variant="outlined"
            onClick={() => googleLogin()}
            disabled={loading || googleLoading}
            startIcon={
              googleLoading ? null : (
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
                </svg>
              )
            }
            sx={{
              mb: 2,
              borderRadius: 1.5,
              fontWeight: 500,
              py: 1.2,
              borderColor: 'divider',
              color: 'text.primary',
              backgroundColor: 'background.paper',
              textTransform: 'none',
              fontSize: '0.9375rem',
              '&:hover': {
                borderColor: 'text.secondary',
                backgroundColor: 'action.hover'
              }
            }}
          >
            {googleLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Continuar com o Google'
            )}
          </Button>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' }, // Empilha em mobile
              justifyContent: 'space-between',
              alignItems: { xs: 'center', sm: 'center' },
              gap: { xs: 1, sm: 0 },
              width: '100%'
            }}
          >
            <Link
              href="/forgot-password"
              variant="body2"
              sx={{
                color: 'primary.light',
                fontWeight: 500,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' }
              }}
            >
              Esqueci minha senha
            </Link>
            <Link
              href="/register"
              variant="body2"
              sx={{
                color: 'primary.light',
                fontWeight: 500,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                textAlign: { xs: 'center', sm: 'right' }
              }}
            >
              Criar conta
            </Link>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginForm;
